import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { getFilterTabs, applyInboxFilter } from '../utils/inboxFilters';

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const diffDays = Math.floor((now - d) / 86400000);
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isYesterday) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const formatMsgTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateDivider = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const avatarColors = ['#25D366','#128C7E','#075E54','#34B7F1','#ECB22E','#E01E5A','#36C5F0','#2EB67D'];
const getColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

export default function Inbox({ agentMode = false }) {
  const [showNewChat, setShowNewChat] = useState(false);
  const [phone, setPhone] = useState("");
  const { selectedBusiness } = useAuth();
  const bizId = selectedBusiness?.id;

  const [showAddContact, setShowAddContact] = useState(false);
  const [showSaveContact, setShowSaveContact] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });
  const [groupData, setGroupData] = useState({ name: '', description: '' });
  const [showDetails, setShowDetails] = useState(false);
  const [newChatTemplate, setNewChatTemplate] = useState('');
  const [newChatVars, setNewChatVars] = useState({});
  const [templates, setTemplates] = useState([]);

  const [conversations, setConversations] = useState([]);
  const [totalConversations, setTotalConversations] = useState(0);
  const [filteredConvs, setFilteredConvs] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [activeTab, setActiveTab] = useState('reply');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryType, setInquiryType] = useState(null);

  const [showQuickReply, setShowQuickReply] = useState(false);
  const [showListMsg, setShowListMsg] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);

  const [quickReplies, setQuickReplies] = useState([
    { id: 1, title: 'Welcome', message: 'Hello! Welcome to our store. How can I help you today?' },
    { id: 2, title: 'Order Received', message: 'Your order has been received and is being processed. Thank you!' },
    { id: 3, title: 'Delivery Update', message: 'Your order is out for delivery and will reach you soon.' },
    { id: 4, title: 'Thank You', message: 'Thank you for shopping with us! Have a great day.' },
  ]);
  const [newQRTitle, setNewQRTitle] = useState('');
  const [newQRMsg, setNewQRMsg] = useState('');
  const [showAddQR, setShowAddQR] = useState(false);

  const [listTitle, setListTitle] = useState('');
  const [listBody, setListBody] = useState('');
  const [listBtnText, setListBtnText] = useState('Choose Option');
  const [listItems, setListItems] = useState(['', '']);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDesc, setPaymentDesc] = useState('');

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!bizId) return;
    try {
      const agentId = agentMode ? localStorage.getItem('agent_id') : null;
      const agentParam = agentId ? `&agent_id=${agentId}` : '';
      const { data } = await api.get(`/messages/conversations?business_id=${bizId}${agentParam}`);
      setConversations(Array.isArray(data) ? data : data.conversations || []);
      setTotalConversations(data.total || data.length || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [bizId]);

  useEffect(() => {
    api.get(`/templates?business_id=${bizId}`).then(r => setTemplates(r.data || [])).catch(() => {});
  }, [bizId]);

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(fetchConversations, 8000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations]);

  useEffect(() => {
    let list = [...conversations];
    if (search) list = list.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search));
    if (filter !== 'all') list = applyInboxFilter(list, filter);
    setFilteredConvs(list);
  }, [conversations, search, filter]);

  const fetchMessages = useCallback(async (phone, showLoader = true) => {
    if (!phone || !bizId) return;
    if (showLoader) setMsgLoading(true);
    try {
      const { data } = await api.get(`/messages/chat?business_id=${bizId}&phone=${phone}`);
      const enriched = await Promise.all(data.map(async (msg) => {
        if (msg.media_url && (msg.media_url.startsWith('/uploads/') || msg.media_url.includes('vbot.co.in/uploads/'))) {
          return msg;
        }
        if (msg.media_id) {
          try {
            const r = await api.get('/messages/media-url?media_id=' + msg.media_id + '&business_id=' + bizId);
            return Object.assign({}, msg, { media_url: r.data.url });
          } catch(e2) { return msg; }
        }
        return msg;
      }));
      const fixed = enriched.map(msg => ({
        ...msg,
        media_url: msg.media_url ? (msg.media_url.startsWith('http') ? msg.media_url : 'https://vbot.co.in' + msg.media_url) : msg.media_url
      }));
      setMessages(fixed);
      await api.post('/messages/mark-read', { business_id: bizId, phone });
      setConversations(prev => prev.map(c => c.phone === phone ? { ...c, unread_count: 0 } : c));
    } catch (e) {
      console.error(e);
    } finally {
      setMsgLoading(false);
    }
  }, [bizId]);

  useEffect(() => {
    // Fetch agents
    if (selectedBusiness?.id) {
      api.get('/agents/' + selectedBusiness.id).then(r => setAgents(r.data.agents || [])).catch(()=>{});
    }
    if (selectedConv) {
      fetchMessages(selectedConv.phone, true);
      if (selectedConv.conv_id) {
        api.get('/agents/assigned/' + selectedConv.conv_id).then(r => setAssignedAgent(r.data.agent || null)).catch(()=>{});
      }
      const poll = setInterval(() => fetchMessages(selectedConv.phone, false), 5000);
      return () => clearInterval(poll);
    }
  }, [selectedConv, fetchMessages]);

  const prevMsgCount = useRef(0);
  const userScrolled = useRef(false);
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
      userScrolled.current = !atBottom;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedConv]);
  useEffect(() => {
    userScrolled.current = false;
    prevMsgCount.current = 0;
  }, [selectedConv]);

  useEffect(() => {
    if (messages.length > 0 && !userScrolled.current) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'instant' });
      }, 50);
    }
    prevMsgCount.current = messages.length;
  }, [messages]);

  const closeAllPopups = () => {
    setShowQuickReply(false);
    setShowListMsg(false);
    setShowPayment(false);
    setShowCatalog(false);
  };

  const handleSend = async () => {
    if (!reply.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      await api.post('/messages/send-text', {
        business_id: bizId,
        phone: selectedConv.phone,
        message: reply.trim()
      });
      setReply('');
      await fetchMessages(selectedConv.phone);
      await fetchConversations();
    } catch (e) {
      alert('Failed to send: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConv) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('business_id', bizId);
      const { data } = await api.post('/messages/upload-media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const mediaType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document';
      await api.post('/messages/send-media', {
        business_id: bizId,
        phone: selectedConv.phone,
        media_url: data.media_url,
        media_type: mediaType,
        filename: file.name
      });
      await fetchMessages(selectedConv.phone);
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleSendListMessage = async () => {
    if (!listTitle || !listBody || listItems.filter(i => i.trim()).length < 1) {
      alert('Please fill title, body and at least 1 option');
      return;
    }
    setSending(true);
    try {
      await api.post('/messages/send-list', {
        business_id: bizId,
        phone: selectedConv.phone,
        header: listTitle,
        body: listBody,
        button_text: listBtnText,
        items: listItems.filter(i => i.trim())
      });
      setShowListMsg(false);
      setListTitle(''); setListBody(''); setListItems(['', '']);
      await fetchMessages(selectedConv.phone);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  const handleSendPayment = async () => {
    if (!paymentAmount) { alert('Enter amount'); return; }
    setSending(true);
    try {
      await api.post('/messages/send-text', {
        business_id: bizId,
        phone: selectedConv.phone,
        message: `💳 Payment Request\nAmount: ₹${paymentAmount}\n${paymentDesc ? 'Description: ' + paymentDesc : ''}\n\nPlease complete your payment.`
      });
      setShowPayment(false);
      setPaymentAmount(''); setPaymentDesc('');
      await fetchMessages(selectedConv.phone);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  const handleSendCatalog = async () => {
    setSending(true);
    try {
      await api.post('/messages/send-text', {
        business_id: bizId,
        phone: selectedConv.phone,
        message: `🛍️ Check out our catalog!\n\nBrowse our latest products and place your order.`
      });
      setShowCatalog(false);
      await fetchMessages(selectedConv.phone);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setSending(false);
    }
  };

  const styles = {
    container: { display: 'flex', height: 'calc(100vh - 56px)', fontFamily: "'DM Sans', sans-serif", background: '#f0f2f5', overflow: 'hidden' },
    leftPanel: { width: 340, minWidth: 300, background: '#fff', borderRight: '1px solid #e9ecef', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    leftHeader: { padding: '14px 16px 10px', borderBottom: '1px solid #f0f2f5' },
    leftTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    badge: { background: '#25D366', color: '#fff', borderRadius: 10, padding: '2px 7px', fontSize: 14, fontWeight: 600 },
    searchBox: { display: 'flex', alignItems: 'center', background: '#f0f2f5', borderRadius: 8, padding: '7px 12px', gap: 8, marginBottom: 10 },
    searchInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: 14, color: '#333', flex: 1, fontFamily: 'inherit' },
    filterTabs: { display: 'flex', gap: 4, flexWrap: 'wrap' },
    filterTab: (active) => ({ padding: '5px 12px', borderRadius: 20, border: active ? '1.5px solid #25D366' : '1.5px solid #e0e0e0', background: active ? '#e8fdf2' : '#fff', color: active ? '#128C7E' : '#666', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer' }),
    convList: { flex: 1, overflowY: 'auto' },
    convItem: (selected) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', background: selected ? '#e8fdf2' : '#fff', borderLeft: selected ? '3px solid #25D366' : '3px solid transparent', borderBottom: '1px solid #f5f5f5' }),
    avatar: (name) => ({ width: 44, height: 44, borderRadius: '50%', background: getColor(name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }),
    convInfo: { flex: 1, minWidth: 0 },
    convName: { fontSize: 14, fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    convMsg: { fontSize: 13, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 },
    convMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 },
    convTime: { fontSize: 11, color: '#aaa' },
    unreadBadge: { background: '#25D366', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center' },
    inquiryBadge: { background: '#eff6ff', color: '#2563eb', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 },
    chatPanel: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minHeight: 0, minWidth: 0 },
    chatHeader: { background: '#fff', borderBottom: '1px solid #e9ecef', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, minHeight: 60, maxHeight: 60 },
    chatHeaderInfo: { flex: 1 },
    chatHeaderName: { fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
    chatHeaderPhone: { fontSize: 14, color: '#888', marginTop: 1 },
    chatHeaderActions: { display: 'flex', gap: 8 },
    iconBtn: { width: 36, height: 36, borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 16 },
    chatMessages: { flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#e5ddd5', display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0 },
    emptyChat: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#e5ddd5', color: '#999', gap: 12 },
    dateDivider: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px 0' },
    datePill: { background: 'rgba(255,255,255,0.85)', borderRadius: 8, padding: '3px 12px', fontSize: 12, color: '#666', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    msgBubble: (dir) => ({ maxWidth: '65%', alignSelf: dir === 'outbound' ? 'flex-end' : 'flex-start', background: dir === 'outbound' ? '#dcf8c6' : '#fff', borderRadius: dir === 'outbound' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', padding: '8px 12px 6px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }),
    msgLabel: (dir) => ({ fontSize: 11, fontWeight: 700, color: dir === 'inbound' ? '#7c3aed' : '#128C7E', marginBottom: 3 }),
    msgText: { fontSize: 14, color: '#1a1a1a', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
    msgMeta: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 3 },
    msgTime: { fontSize: 11, color: '#999' },
    replyPanel: { background: '#fff', borderTop: '1px solid #e9ecef', position: 'relative' },
    replyTabs: { display: 'flex', borderBottom: '1px solid #f0f2f5', padding: '0 16px' },
    replyTab: (active) => ({ padding: '10px 16px', borderBottom: active ? '2px solid #25D366' : '2px solid transparent', color: active ? '#128C7E' : '#999', fontWeight: active ? 600 : 400, fontSize: 14, cursor: 'pointer' }),
    replyToolbar: { display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 6, borderBottom: '1px solid #f5f5f5' },
    toolbarBtn: (active) => ({ width: 32, height: 32, borderRadius: 6, border: active ? '1.5px solid #25D366' : 'none', background: active ? '#e8fdf2' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? '#128C7E' : '#666', fontSize: 16 }),
    replyInputArea: { display: 'flex', alignItems: 'flex-end', padding: '8px 12px 12px', gap: 10 },
    replyTextarea: { flex: 1, border: 'none', outline: 'none', resize: 'none', fontSize: 14, fontFamily: 'inherit', color: '#1a1a1a', lineHeight: 1.5, maxHeight: 120, minHeight: 40, background: 'transparent', padding: '4px 0' },
    sendBtn: (active) => ({ width: 42, height: 42, borderRadius: '50%', border: 'none', background: active ? '#25D366' : '#e0e0e0', color: '#fff', cursor: active ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }),
    rightPanel: { width: 280, minWidth: 240, background: '#fff', borderLeft: '1px solid #e9ecef', overflowY: 'auto', padding: '20px 16px' },
    rightTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #f0f2f5' },
    contactAvatar: (name) => ({ width: 64, height: 64, borderRadius: '50%', background: getColor(name), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, margin: '0 auto 12px' }),
    contactName: { fontSize: 16, fontWeight: 700, color: '#1a1a1a', textAlign: 'center', marginBottom: 4 },
    contactPhone: { fontSize: 13, color: '#888', textAlign: 'center', marginBottom: 16 },
    viewDetailsBtn: { display: 'block', width: '100%', padding: '9px', background: '#128C7E', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 12, fontFamily: 'inherit' },
    detailRow: { marginBottom: 14 },
    detailLabel: { fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 },
    detailValue: { fontSize: 14, color: '#333', fontWeight: 500 },
    optedBadge: (yes) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, background: yes ? '#e8fdf2' : '#fff0f0', color: yes ? '#128C7E' : '#e53935', fontSize: 12, fontWeight: 600 }),
    loadingWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: '#aaa', fontSize: 14 },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    popup: { background: '#fff', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    popupTitle: { fontSize: 16, fontWeight: 700, color: '#1a1a1a', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    popupInput: { width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', marginBottom: 10, boxSizing: 'border-box' },
    popupBtn: (primary) => ({ padding: '9px 20px', borderRadius: 8, border: 'none', background: primary ? '#25D366' : '#f0f2f5', color: primary ? '#fff' : '#333', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }),
    qrItem: { padding: '10px 14px', border: '1px solid #e9ecef', borderRadius: 8, marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  };

  const groupedMessages = messages.reduce((acc, msg) => {
    const key = new Date(msg.created_at).toDateString();
    if (!acc[key]) acc[key] = [];
    acc[key].push(msg);
    return acc;
  }, {});

  return (
    <div style={styles.container} onClick={() => closeAllPopups()}>

      {showQuickReply && (
        <div style={styles.overlay} onClick={e => e.stopPropagation()}>
          <div style={styles.popup}>
            <div style={styles.popupTitle}>
              <span>⚡ Quick Replies</span>
              <span style={{ cursor: 'pointer', color: '#aaa', fontSize: 20 }} onClick={() => setShowQuickReply(false)}>✕</span>
            </div>
            {quickReplies.map(qr => (
              <div key={qr.id} style={styles.qrItem} onClick={() => { setReply(qr.message); setShowQuickReply(false); }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{qr.title}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{qr.message.slice(0, 50)}...</div>
                </div>
                <span style={{ color: '#25D366', fontSize: 18 }}>→</span>
              </div>
            ))}
            <div style={{ marginTop: 12, borderTop: '1px solid #f0f2f5', paddingTop: 12 }}>
              {showAddQR ? (
                <div>
                  <input style={styles.popupInput} placeholder="Title" value={newQRTitle} onChange={e => setNewQRTitle(e.target.value)} />
                  <textarea style={{ ...styles.popupInput, minHeight: 70, resize: 'none' }} placeholder="Message" value={newQRMsg} onChange={e => setNewQRMsg(e.target.value)} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={styles.popupBtn(true)} onClick={() => {
                      if (newQRTitle && newQRMsg) {
                        setQuickReplies(prev => [...prev, { id: Date.now(), title: newQRTitle, message: newQRMsg }]);
                        setNewQRTitle(''); setNewQRMsg(''); setShowAddQR(false);
                      }
                    }}>Save</button>
                    <button style={styles.popupBtn(false)} onClick={() => setShowAddQR(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button style={styles.popupBtn(false)} onClick={() => setShowAddQR(true)}>+ Add New Quick Reply</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showListMsg && (
        <div style={styles.overlay} onClick={e => e.stopPropagation()}>
          <div style={styles.popup}>
            <div style={styles.popupTitle}>
              <span>☰ List Message</span>
              <span style={{ cursor: 'pointer', color: '#aaa', fontSize: 20 }} onClick={() => setShowListMsg(false)}>✕</span>
            </div>
            <input style={styles.popupInput} placeholder="Header" value={listTitle} onChange={e => setListTitle(e.target.value)} />
            <input style={styles.popupInput} placeholder="Body text" value={listBody} onChange={e => setListBody(e.target.value)} />
            <input style={styles.popupInput} placeholder="Button text" value={listBtnText} onChange={e => setListBtnText(e.target.value)} />
            {listItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={{ ...styles.popupInput, marginBottom: 0, flex: 1 }} placeholder={`Option ${i + 1}`} value={item} onChange={e => { const arr = [...listItems]; arr[i] = e.target.value; setListItems(arr); }} />
                {listItems.length > 2 && <button style={{ ...styles.popupBtn(false), padding: '8px 12px' }} onClick={() => setListItems(listItems.filter((_, j) => j !== i))}>✕</button>}
              </div>
            ))}
            {listItems.length < 10 && <button style={{ ...styles.popupBtn(false), marginBottom: 12 }} onClick={() => setListItems([...listItems, ''])}>+ Add Option</button>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={styles.popupBtn(true)} onClick={handleSendListMessage} disabled={sending}>{sending ? 'Sending...' : 'Send List'}</button>
              <button style={styles.popupBtn(false)} onClick={() => setShowListMsg(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPayment && (
        <div style={styles.overlay} onClick={e => e.stopPropagation()}>
          <div style={styles.popup}>
            <div style={styles.popupTitle}>
              <span>💳 Payment Link</span>
              <span style={{ cursor: 'pointer', color: '#aaa', fontSize: 20 }} onClick={() => setShowPayment(false)}>✕</span>
            </div>
            <input style={styles.popupInput} placeholder="Amount (₹)" type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} />
            <input style={styles.popupInput} placeholder="Description (optional)" value={paymentDesc} onChange={e => setPaymentDesc(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.popupBtn(true)} onClick={handleSendPayment} disabled={sending}>{sending ? 'Sending...' : 'Send'}</button>
              <button style={styles.popupBtn(false)} onClick={() => setShowPayment(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showCatalog && (
        <div style={styles.overlay} onClick={e => e.stopPropagation()}>
          <div style={styles.popup}>
            <div style={styles.popupTitle}>
              <span>🛍️ Catalog</span>
              <span style={{ cursor: 'pointer', color: '#aaa', fontSize: 20 }} onClick={() => setShowCatalog(false)}>✕</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.popupBtn(true)} onClick={handleSendCatalog} disabled={sending}>{sending ? 'Sending...' : 'Send Catalog'}</button>
              <button style={styles.popupBtn(false)} onClick={() => setShowCatalog(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showInquiry && selectedConv && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowInquiry(false); setInquiryType(null); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: 500, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.25)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>🔍 Inquiry — {selectedConv.name || selectedConv.phone}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{selectedConv.phone}</div>
              </div>
              <button onClick={() => { setShowInquiry(false); setInquiryType(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: 10 }}>
              <button onClick={() => setInquiryType('inbound')} style={{ flex: 1, padding: '10px', borderRadius: 10, border: inquiryType === 'inbound' ? '2px solid #7c3aed' : '1.5px solid #e0e0e0', background: inquiryType === 'inbound' ? '#f5f3ff' : '#fff', color: inquiryType === 'inbound' ? '#7c3aed' : '#555', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                📥 Inbound
                <div style={{ fontSize: 11, fontWeight: 400, marginTop: 2, color: inquiryType === 'inbound' ? '#7c3aed' : '#aaa' }}>Client msg அனுப்பினாங்க</div>
              </button>
              <button onClick={() => setInquiryType('outbound')} style={{ flex: 1, padding: '10px', borderRadius: 10, border: inquiryType === 'outbound' ? '2px solid #0891b2' : '1.5px solid #e0e0e0', background: inquiryType === 'outbound' ? '#ecfeff' : '#fff', color: inquiryType === 'outbound' ? '#0891b2' : '#555', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                📤 Outbound
                <div style={{ fontSize: 11, fontWeight: 400, marginTop: 2, color: inquiryType === 'outbound' ? '#0891b2' : '#aaa' }}>நாம் contact பண்றோம்</div>
              </button>
            </div>
            <div ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', background: '#e5ddd5', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>No messages yet</div>
              ) : Object.entries(groupedMessages).map(([dateKey, msgs]) => (
                <div key={dateKey}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '6px 0' }}>
                    <span style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 8, padding: '2px 10px', fontSize: 11, color: '#666' }}>{formatDateDivider(msgs[0].created_at)}</span>
                  </div>
                  {msgs.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', marginBottom: 4 }}>
                      <div style={{ maxWidth: '70%', alignSelf: msg.direction === 'outbound' ? 'flex-end' : 'flex-start', background: msg.direction === 'outbound' ? '#dcf8c6' : '#fff', borderRadius: msg.direction === 'outbound' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', padding: '7px 11px 5px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', fontSize: 13 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: msg.direction === 'inbound' ? '#7c3aed' : '#128C7E', marginBottom: 2 }}>
                          {msg.direction === 'inbound' ? '👤 Customer' : '✉️ You'}
                        </div>
                        {msg.message && <div style={{ color: '#1a1a1a', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.message}</div>}
                        <div style={{ fontSize: 10, color: '#999', textAlign: 'right', marginTop: 2 }}>{formatMsgTime(msg.wa_timestamp || msg.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f2f5', background: '#f8fafc', display: 'flex', gap: 10, alignItems: 'center' }}>
              {!inquiryType ? (
                <div style={{ flex: 1, fontSize: 13, color: '#aaa', textAlign: 'center' }}>⬆️ Inbound அல்லது Outbound select பண்ணுங்க</div>
              ) : (
                <>
                  <div style={{ flex: 1, fontSize: 13, color: '#555' }}>
                    {inquiryType === 'inbound' ? '📥 Inbound inquiry — client msg பாத்து reply பண்ணலாம்' : '📤 Outbound inquiry — client-கிட்ட details கேக்கலாம்'}
                  </div>
                  <button onClick={() => { setShowInquiry(false); setInquiryType(null); }} style={{ padding: '9px 20px', background: inquiryType === 'inbound' ? '#7c3aed' : '#0891b2', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                    ✓ Confirm
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LEFT PANEL */}
      <div style={styles.leftPanel}>
        <div style={styles.leftHeader}>
          <div style={styles.leftTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Inbox
              {totalConversations > 0 && <span style={styles.badge}>{totalConversations}</span>}
            </div>
          </div>
          <div style={styles.searchBox}>
            <span style={{ color: '#aaa', fontSize: 14 }}>🔍</span>
            <input style={styles.searchInput} placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={styles.filterTabs}>
              {getFilterTabs(bizId, conversations).map(f => (
                <button key={f.id} style={styles.filterTab(filter === f.id)} onClick={() => setFilter(f.id)}>
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowNewChat(true)} style={{ background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 20, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              ➕ New
            </button>
          </div>
        </div>

        <div style={styles.convList}>
          {loading ? (
            <div style={styles.loadingWrap}>Loading chats...</div>
          ) : filteredConvs.length === 0 ? (
            <div style={styles.loadingWrap}>No conversations</div>
          ) : filteredConvs.map((conv, i) => (
            <div key={i} style={styles.convItem(selectedConv?.phone === conv.phone)} onClick={() => setSelectedConv(conv)}>
              <div style={{ position: 'relative' }}>
                <div style={styles.avatar(conv.name || conv.phone)}>{getInitials(conv.name || conv.phone)}</div>
                {(conv.has_inbound === 1 || conv.has_inbound === true) && (
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: '#7c3aed', border: '2px solid #fff' }} />
                )}
              </div>
              <div style={styles.convInfo}>
                <div style={styles.convName}>{conv.name || conv.phone}</div>
                <div style={styles.convMsg}>
                  {conv.last_direction === 'inbound' ? '💬 ' : ''}{(() => { const m = conv.last_message || 'No messages yet'; if(m === '[document]') return '📄 Document'; if(m === '[image]') return '🖼️ Image'; if(m === '[video]') return '🎥 Video'; if(m === '[audio]') return '🎵 Audio'; return m; })()}
                </div>
              </div>
              <div style={styles.convMeta}>
                <span style={styles.convTime}>{formatTime(conv.last_message_time || conv.created_at)}</span>
                {conv.unread_count > 0 && <span style={styles.unreadBadge}>{conv.unread_count}</span>}
                {conv.window_opened_at && (() => {
                  const opened = new Date(conv.window_opened_at).getTime();
                  const hoursLeft = 24 - (Date.now() - opened) / 3600000;
                  if (hoursLeft <= 0) return <span style={{ background: '#FEE2E2', color: '#B91C1C', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🔴 Expired</span>;
                  if (hoursLeft <= 6) return <span style={{ background: '#FEF3C7', color: '#B45309', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🟡 {Math.ceil(hoursLeft)}h left</span>;
                  return <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🟢 {Math.ceil(hoursLeft)}h left</span>;
                })()}
                {bizId !== 26 && conv.last_msg_type === 'checkout_creation' && <span style={{ background: '#FFF3E0', color: '#E65100', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🛒 Cart</span>}
                {(conv.last_msg_type === 'shopify' || conv.has_order == 1) && <span style={{ background: '#E8F5E9', color: '#2E7D32', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>📦 Order</span>}
                {(conv.last_msg_type === 'template' || conv.last_msg_type === 'invoice') && (() => {
                  if ((bizId === 22 || bizId === 33) && conv.last_template_name) {
                    return <span style={{ background: '#F3F4F6', color: '#374151', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🏷️ {conv.last_template_name}</span>;
                  }
                  const m = (conv.last_message || '').toLowerCase();
                  if (m.includes('bus ticket')) return <span style={{ background: '#E0F2FE', color: '#0369A1', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🚌 Ticket</span>;
                  if (m.includes('cashback')) return <span style={{ background: '#FEF9C3', color: '#854D0E', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>💰 Cashback</span>;
                  if (m.includes('location')) return <span style={{ background: '#DCFCE7', color: '#166534', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>📍 Location</span>;
                  return <span style={{ background: '#EDE9FE', color: '#6D28D9', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🧾 Invoice</span>;
                })()}
                {conv.last_msg_type === 'sale' && <span style={{ background: '#FCE7F3', color: '#BE185D', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🛍️ Sale</span>}
                {conv.last_msg_type === 'inv' && <span style={{ background: '#DBEAFE', color: '#1D4ED8', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🧾 Inv</span>}
                {(conv.last_msg_type === 'shipped' || conv.last_msg_type === 'shopify_fulfillment') && <span style={{ background: '#FFF7ED', color: '#C2410C', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🚚 Shipped</span>}
                {conv.last_msg_type === 'Tracking' && <span style={{ background: '#DBEAFE', color: '#1D4ED8', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>📦 Tracking</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CHAT PANEL */}
      <div style={styles.chatPanel}>
        {!selectedConv ? (
          <div style={styles.emptyChat}>
            <div style={{ fontSize: 48 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666' }}>Select a conversation</div>
            <div style={{ fontSize: 13, color: '#aaa' }}>Choose from your inbox to start chatting</div>
          </div>
        ) : (
          <>
            <div style={styles.chatHeader}>
              <div style={{ ...styles.avatar(selectedConv.name || selectedConv.phone), width: 38, height: 38, fontSize: 13 }}>{getInitials(selectedConv.name || selectedConv.phone)}</div>
              <div style={styles.chatHeaderInfo}>
                <div style={styles.chatHeaderName}>{selectedConv.name || selectedConv.phone}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={styles.chatHeaderPhone}>{selectedConv.phone}</span>
                  {(selectedConv.has_inbound === 1 || selectedConv.has_inbound === true) && (
                    <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>Inquiry</span>
                  )}
                </div>
              </div>
              <div style={styles.chatHeaderActions}>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowAssignDropdown(v => !v)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    👤 {assignedAgent ? assignedAgent.name : 'Assign'}
                  </button>
                  {showAssignDropdown && (
                    <div style={{ position: 'absolute', top: '110%', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 180, padding: 8 }}>
                      {agents.length === 0 && <div style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>No agents found</div>}
                      {agents.map(agent => (
                        <div key={agent.id} onClick={async () => {
                          await api.post('/agents/assign', { conv_id: selectedConv.conv_id, agent_id: agent.id });
                          setAssignedAgent(agent);
                          setShowAssignDropdown(false);
                        }} style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 7, fontSize: 13, fontWeight: assignedAgent?.id === agent.id ? 700 : 400, background: assignedAgent?.id === agent.id ? '#ede9fe' : 'transparent', color: assignedAgent?.id === agent.id ? '#7c3aed' : '#374151' }}>
                          👤 {agent.name}
                        </div>
                      ))}
                      {assignedAgent && <div onClick={async () => {
                        await api.post('/agents/assign', { conv_id: selectedConv.conv_id, agent_id: null });
                        setAssignedAgent(null);
                        setShowAssignDropdown(false);
                      }} style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 7, fontSize: 13, color: '#e53e3e', borderTop: '1px solid #f0f0f0', marginTop: 4 }}>
                        ✕ Unassign
                      </div>}
                    </div>
                  )}
                </div>
                {messages.some(m => !m.msg_type || m.msg_type === 'text' || m.msg_type === 'image') && (
                  <button onClick={() => { setShowInquiry(true); setInquiryType(null); }} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                    🔍 Inquiry
                  </button>
                )}
                <button style={styles.iconBtn}>👤</button>
                <button style={styles.iconBtn}>📞</button>
                <button style={styles.iconBtn}>⋯</button>
              </div>
            </div>

            <div style={styles.chatMessages} ref={chatContainerRef}>
              {msgLoading ? (
                <div style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>Loading messages...</div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>No messages yet</div>
              ) : Object.entries(groupedMessages).map(([dateKey, msgs]) => (
                <div key={dateKey}>
                  <div style={styles.dateDivider}>
                    <span style={styles.datePill}>{formatDateDivider(msgs[0].created_at)}</span>
                  </div>
                  {msgs.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', marginBottom: 4 }}>
                      <div style={styles.msgBubble(msg.direction)}>
                        <div style={styles.msgLabel(msg.direction)}>
                          {msg.direction === 'inbound' ? '👤 Customer' : '✉️ You'}
                        </div>
                        {msg.media_url && (msg.media_type === 'image' || msg.msg_type === 'image' || msg.message === '[image]') && (() => {
                          const fullUrl = msg.media_url.startsWith('http') ? msg.media_url : 'https://vbot.co.in' + msg.media_url;
                          return <img src={fullUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 4, cursor: 'pointer' }} onClick={() => window.open(fullUrl, '_blank')} />;
                        })()}
                        {msg.media_url && msg.media_type === 'video' && (
                          <video controls style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 4 }}>
                            <source src={msg.media_url} type="audio/webm" />
                          </video>
                        )}
                        {msg.media_url && (msg.media_type === 'audio' || msg.msg_type === 'audio' || msg.message === '[audio]') && (() => {
                          const aid = 'audio-' + msg.id;
                          return (
                          <div style={{ display:'flex', alignItems:'center', gap:10, maxWidth:260, padding:'4px 0', overflow:'hidden' }}>
                            <audio id={aid} src={msg.media_url} preload="metadata"
                              onLoadedMetadata={e => { const el = e.target; if(el.duration === Infinity || isNaN(el.duration)) { el.currentTime = 1e101; el.ontimeupdate = () => { el.ontimeupdate = null; el.currentTime = 0; const d = document.getElementById('dur-'+msg.id); if(d) d.textContent = isNaN(el.duration) ? '0:00' : Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); }; } else { const d = document.getElementById('dur-'+msg.id); if(d) d.textContent = Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); } }}
                              onTimeUpdate={e => { const p = document.getElementById('prog-'+msg.id); if(p && e.target.duration) p.style.width = (e.target.currentTime/e.target.duration*100)+'%'; const d = document.getElementById('dur-'+msg.id); if(d && !e.target.paused) d.textContent = Math.floor(e.target.currentTime/60)+':'+(Math.floor(e.target.currentTime%60)+'').padStart(2,'0'); }}
                              onEnded={e => { const p = document.getElementById('prog-'+msg.id); if(p) p.style.width='0%'; const ic = document.getElementById('plic-'+msg.id); if(ic) ic.innerHTML='<polygon points=\'6,4 18,12 6,20\' fill=\'white\'/>'; const d = document.getElementById('dur-'+msg.id); const el = document.getElementById(aid); if(d && el) d.textContent = Math.floor(el.duration/60)+':'+(Math.floor(el.duration%60)+'').padStart(2,'0'); }} />
                            <div onClick={() => { const a = document.getElementById(aid); const ic = document.getElementById('plic-'+msg.id); if(a.paused) { a.play(); if(ic) ic.innerHTML='<rect x=\'6\' y=\'4\' width=\'4\' height=\'16\' fill=\'white\'/><rect x=\'14\' y=\'4\' width=\'4\' height=\'16\' fill=\'white\'/>'; } else { a.pause(); if(ic) ic.innerHTML='<polygon points=\'6,4 18,12 6,20\' fill=\'white\'/>'; } }}
                              style={{ width:46, height:46, borderRadius:'50%', background:'#FF9800', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, boxShadow:'0 2px 6px rgba(0,0,0,0.2)' }}>
                              <svg id={'plic-'+msg.id} width="24" height="24" viewBox="0 0 24 24">
                                <polygon points="6,4 18,12 6,20" fill="white"/>
                              </svg>
                            </div>
                            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
                              <div style={{ position:'relative', height:4, background:'rgba(0,0,0,0.15)', borderRadius:2, cursor:'pointer' }}
                                onClick={e => { const a = document.getElementById(aid); if(a.duration) { const rect = e.currentTarget.getBoundingClientRect(); const pct = (e.clientX - rect.left)/rect.width; a.currentTime = pct * a.duration; } }}>
                                <div id={'prog-'+msg.id} style={{ height:'100%', background: msg.direction==='outbound' ? '#075E54' : '#919191', borderRadius:2, width:'0%', transition:'width 0.5s linear' }}/>
                              </div>
                              <span id={'dur-'+msg.id} style={{ fontSize:11, color: msg.direction==='outbound' ? '#075E54' : '#666' }}>0:00</span>
                            </div>
                          </div>
                          );
                        })()}
                        {msg.media_url && msg.media_type === 'document' && (
                          <>
                            <a href={msg.media_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f4ff', borderRadius: 8, padding: '8px 12px', marginBottom: 4, textDecoration: 'none', border: '1px solid #d0d8ff' }}>
                              <span style={{ fontSize: 24 }}>📄</span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{msg.media_filename || 'Document'}</div>
                                <div style={{ fontSize: 11, color: '#666' }}>Click to open</div>
                              </div>
                            </a>
                            {msg.message && !['[document]','[image]','[video]','[audio]','[sticker]','[unsupported]','[reaction]','[location]','[contacts]'].includes(msg.message) && (
                              <div style={{ padding: '4px 12px 2px', fontSize: 13, color: '#1a1a1a', whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                            )}
                          </>
                        )}
                        {msg.message && !(msg.media_url && (msg.media_type === 'image' || msg.media_type === 'video' || msg.media_type === 'audio' || msg.media_type === 'document' || msg.media_type === 'sticker' || ['[image]','[video]','[audio]','[document]','[sticker]','[unsupported]','[reaction]','[location]','[contacts]'].includes(msg.message))) && <div style={styles.msgText}>{msg.message}</div>}
                        {msg.media_url && msg.template_body && (
                          <div style={{ padding: '4px 12px 2px', fontSize: 13, color: '#1a1a1a' }}>{msg.template_body}</div>
                        )}
                        {msg.buttons && (() => {
                          let btnList = [];
                          try { btnList = JSON.parse(msg.buttons); } catch(e) { btnList = []; }
                          if (!btnList.length) return null;
                          return (
                            <div style={{ marginTop: 6, borderTop: '1px solid #e0e0e0' }}>
                              {btnList.map((btn, i) => (
                                <div key={i} style={{ padding: '8px 12px', textAlign: 'center', color: '#0093FF', fontWeight: 600, fontSize: 13, borderTop: i > 0 ? '1px solid #e0e0e0' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                  {btn.type === 'PHONE_NUMBER' && <span>📞</span>}
                                  {btn.type === 'URL' && <span>🔗</span>}
                                  {btn.type === 'QUICK_REPLY' && <span>↩️</span>}
                                  {btn.text}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {msg.footer && <div style={{ padding: '4px 12px 6px', fontSize: 11, color: '#8696a0', borderTop: '1px solid #f0f0f0', marginTop: 4 }}>{msg.footer}</div>}
                        {(msg.buttons_json || msg.buttons) && (() => {
                          try {
                            const btns = typeof (msg.buttons_json || msg.buttons) === 'string' ? JSON.parse(msg.buttons_json || msg.buttons) : (msg.buttons_json || msg.buttons);
                            if (Array.isArray(btns) && btns.length > 0) {
                              return <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 6 }}>
                                {btns.map((btn, bi) => (
                                  <div key={bi} style={{ padding: '8px 12px', textAlign: 'center', color: '#0891b2', fontSize: 13, fontWeight: 600, borderBottom: bi < btns.length-1 ? '1px solid #f0f0f0' : 'none', cursor: 'default' }}>
                                    ↩ {btn.text || btn.title || btn}
                                  </div>
                                ))}
                              </div>;
                            }
                          } catch(e) {}
                          return null;
                        })()}
                        <div style={styles.msgMeta}>
                          <span style={styles.msgTime}>{formatMsgTime(msg.wa_timestamp || msg.created_at)}</span>
                          {msg.direction === 'outbound' && (
                            <span style={{ color: msg.status === 'read' ? '#34B7F1' : '#aaa', fontSize: 12 }}>✓✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div style={styles.replyPanel} onClick={e => e.stopPropagation()}>
              <div style={styles.replyTabs}>
                {['reply', 'notes'].map(t => (
                  <div key={t} style={styles.replyTab(activeTab === t)} onClick={() => setActiveTab(t)}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </div>
                ))}
              </div>
              <div style={styles.replyToolbar}>
                <button title="Quick Reply" style={styles.toolbarBtn(showQuickReply)} onClick={() => { closeAllPopups(); setShowQuickReply(v => !v); }}>⚡</button>
                <button title="List Message" style={styles.toolbarBtn(showListMsg)} onClick={() => { closeAllPopups(); setShowListMsg(v => !v); }}>☰</button>
                <button title="Payment" style={styles.toolbarBtn(showPayment)} onClick={() => { closeAllPopups(); setShowPayment(v => !v); }}>💳</button>
                <button title="Catalog" style={styles.toolbarBtn(showCatalog)} onClick={() => { closeAllPopups(); setShowCatalog(v => !v); }}>🛍️</button>
                <button title="Attachment" style={styles.toolbarBtn(false)} onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                  {uploadingFile ? '⏳' : '📎'}
                </button>
                <button title="Templates" style={styles.toolbarBtn(false)}>📋</button>
                <button title="Emoji" style={styles.toolbarBtn(false)}>😊</button>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
              </div>
              <div style={styles.replyInputArea}>
                <textarea
                  style={styles.replyTextarea}
                  placeholder={activeTab === 'reply' ? 'Type a message...' : 'Add a note...'}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  rows={1}
                />
                <button style={styles.sendBtn(reply.trim().length > 0 && !sending)} onClick={handleSend} disabled={!reply.trim() || sending}>
                  {sending ? '⏳' : '➤'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* RIGHT PANEL */}
      {selectedConv && (
        <div style={styles.rightPanel}>
          <div style={styles.rightTitle}>Contact Details</div>
          <div style={styles.contactAvatar(selectedConv.name || selectedConv.phone)}>
            {getInitials(selectedConv.name || selectedConv.phone)}
          </div>
          <div style={styles.contactName}>{selectedConv.name || 'Unknown'}</div>
          <div style={styles.contactPhone}>{selectedConv.phone}</div>
          {(selectedConv.has_inbound === 1 || selectedConv.has_inbound === true) && (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 10 }}>💬 Has Inquiry</span>
            </div>
          )}
          <button style={{ ...styles.viewDetailsBtn, background: showDetails ? '#0a6560' : '#128C7E' }} onClick={() => setShowDetails(v => !v)}>
            {showDetails ? '✕ Hide Details' : '☰ View Details'}
          </button>
          {!showDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <button onClick={() => setShowAddContact(true)} style={{ width: '100%', padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>➕ Add Contact</button>
              <button onClick={() => setShowSaveContact(true)} style={{ width: '100%', padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>💾 Save Contact</button>
              <button onClick={() => setShowCreateGroup(true)} style={{ width: '100%', padding: '10px', background: '#a855f7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>👥 Create Group</button>
            </div>
          )}
          {showDetails && (
            <div>
              <div style={styles.detailRow}>
                <div style={styles.detailLabel}>CONTACT</div>
                <div style={styles.detailValue}>+{selectedConv.phone}</div>
              </div>
              <div style={styles.detailRow}>
                <div style={styles.detailLabel}>WhatsApp Opted</div>
                <span style={styles.optedBadge(true)}>Yes</span>
              </div>
              <div style={styles.detailRow}>
                <div style={styles.detailLabel}>Unread Messages</div>
                <div style={styles.detailValue}>{selectedConv.unread_count || 0}</div>
              </div>
              <div style={styles.detailRow}>
                <div style={styles.detailLabel}>Last Message</div>
                <div style={styles.detailValue}>{formatTime(selectedConv.last_message_time || selectedConv.created_at)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {showAddContact && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAddContact(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>➕ Add New Contact</h2>
              <button onClick={() => setShowAddContact(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}>✕</button>
            </div>
            <input type="text" value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 }} placeholder="Contact Name *" />
            <input type="tel" value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 }} placeholder="Phone Number *" />
            <input type="email" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 16 }} placeholder="Email (Optional)" />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowAddContact(false)} style={{ flex: 1, padding: '10px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { alert('✅ Contact Added!'); setNewContact({ name: '', phone: '', email: '' }); setShowAddContact(false); }} style={{ flex: 1, padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {showSaveContact && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSaveContact(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>💾 Save Contact</h2>
            <p style={{ fontSize: 14, color: '#555', marginBottom: 20 }}>Save '{selectedConv?.name || selectedConv?.phone}' to contacts?</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowSaveContact(false)} style={{ flex: 1, padding: '10px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { alert('✅ Saved!'); setShowSaveContact(false); }} style={{ flex: 1, padding: '10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showCreateGroup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowCreateGroup(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>👥 Create Group</h2>
            <input type="text" value={groupData.name} onChange={e => setGroupData({ ...groupData, name: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 }} placeholder="Group Name *" />
            <textarea value={groupData.description} onChange={e => setGroupData({ ...groupData, description: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', resize: 'none', minHeight: 70, marginBottom: 16 }} placeholder="Description (Optional)" />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCreateGroup(false)} style={{ flex: 1, padding: '10px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { alert('✅ Group Created!'); setGroupData({ name: '', description: '' }); setShowCreateGroup(false); }} style={{ flex: 1, padding: '10px', background: '#a855f7', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {showNewChat && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowNewChat(false); setPhone(''); setNewChatTemplate(''); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📱 New Chat</h2>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} onBlur={async e => {
              const p = e.target.value.replace(/[^0-9]/g, '');
              if (p.length >= 10 && newChatTemplate === 'order_shipped_tracking') {
                try {
                  const r = await api.get('/shopify/tracking-by-phone-live', { params: { phone: p, business_id: bizId } });
                  if (r.data.found) setNewChatVars(prev => ({ ...prev, '1': r.data.name || '', '2': r.data.tracking || '' }));
                } catch(e) {}
              }
            }} style={{ width: '100%', padding: '12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 12 }} placeholder="+91 98765 43210" />
            <select value={newChatTemplate} onChange={async e => {
              setNewChatTemplate(e.target.value);
              if (e.target.value === 'order_shipped_tracking' && phone.replace(/[^0-9]/g,'').length >= 10) {
                try {
                  const p = phone.replace(/[^0-9]/g,'');
                  const r = await api.get('/shopify/tracking-by-phone-live', { params: { phone: p, business_id: bizId } });
                  if (r.data.found) setNewChatVars(prev => ({ ...prev, '1': r.data.name || '', '2': r.data.tracking || '' }));
                } catch(e) {}
              }
            }} style={{ width: '100%', padding: '12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 16, background: '#fff' }}>
              <option value="">-- Select Template --</option>
              {templates.filter(t => t.is_new_chat == 1 || t.is_new_chat === true).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
            {newChatTemplate && (() => {
              const tpl = templates.find(t => t.name === newChatTemplate);
              const body = tpl?.components?.find(c => c.type === 'BODY')?.text || '';
              const matches = [...body.matchAll(/\{\{(\d+)\}\}/g)];
              return matches.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  {matches.map(m => (
                    <input key={m[1]} placeholder={`Variable {{${m[1]}}}`}
                      value={newChatVars[m[1]] || ''}
                      onChange={e => setNewChatVars(prev => ({ ...prev, [m[1]]: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }} />
                  ))}
                </div>
              ) : null;
            })()}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setShowNewChat(false); setPhone(''); setNewChatTemplate(''); setNewChatVars({}); }} style={{ flex: 1, padding: '10px', background: '#f0f2f5', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={async () => { if (!phone.trim() || !newChatTemplate) return; const cleanPhone = phone.replace(/[^0-9]/g, ''); const savedVars = {...newChatVars}; setPhone(''); setNewChatTemplate(''); setShowNewChat(false); setNewChatVars({}); try { await api.post('/contacts', { phone: cleanPhone, name: cleanPhone, business_id: bizId }); } catch(e) {} try { const varParams = Object.keys(savedVars).sort().map(k => ({ type: 'text', text: savedVars[k] }));
              await api.post('/messages/send', { business_id: bizId, phone: cleanPhone, template_name: newChatTemplate, language: 'en_US', components: (() => {
                const comps = [];
                if (varParams.length) comps.push({ type: 'body', parameters: varParams });
                const tpl = templates.find(t => t.name === newChatTemplate); const btnComp = tpl?.components?.find(c => c.type === 'BUTTONS');
                if (btnComp) comps.push(btnComp);
                return comps.length ? comps : undefined;
              })() }); } catch(e) { alert('Failed: ' + e.message); } await fetchConversations(); setTimeout(() => setSelectedConv({ phone: cleanPhone, name: cleanPhone }), 500); }} disabled={!phone.trim() || !newChatTemplate} style={{ flex: 1, padding: '10px', background: (phone.trim() && newChatTemplate) ? '#25D366' : '#ccc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: (phone.trim() && newChatTemplate) ? 'pointer' : 'not-allowed' }}>Send Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}